import { useIsMobile } from '@/hooks/useIsMobile';
import { JobDetailsDesktop } from './JobDetailsDesktop';
import { JobDetailsMobile } from './JobDetailsMobile';
import { JobDTO, JobListItemDTO } from '@/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '@/lib/api';
import { Loader2, SearchX } from 'lucide-react';
import { useJobInvalidation } from '@/hooks/useJobInvalidation';
import { useModalLayer, LayerDepthContext } from '@/lib/layerManager';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { OfflineFallback } from './ui/OfflineFallback';
import { useNotifications, notificationService } from './ui/NotificationSystem';

interface JobDetailsModalProps {
  isOpen?: boolean;
  onClose: () => void;
  job?: JobDTO | JobListItemDTO | null;
  jobId?: string | null;
  onDelete?: (job: JobDTO) => void;
  onCancelJob?: (job: JobDTO, reason?: string) => void;
  onUncancelJob?: (job: JobDTO) => void;
  onJobUpdated?: (updatedJob: JobDTO) => void;
  openedFromInvoice?: boolean;  // Opened from InvoiceDetailsModal — Invoice button closes instead of re-opening
  openedFromPastJobs?: boolean; // Opened via "past jobs" drill-in
  /** Extra keys merged into the modal's history.pushState (e.g. voip_job_id for browser-back). */
  historyState?: Record<string, unknown>;
}

export function JobDetailsModal(props: JobDetailsModalProps) {
  const isMobile = useIsMobile();
  const resolvedJobId = props.jobId || props.job?.id;
  const isOpen = props.isOpen ?? true;
  const layer = useModalLayer({
    enabled: isOpen && !isMobile,
    onClose: props.onClose,
    historyState: { jobDetailsModal: true, ...props.historyState },
  });
  useJobInvalidation(resolvedJobId);
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();

  // The Cancel / Un-cancel buttons only render when these callbacks exist, so a job opened
  // from anywhere but JobsPage (messages, calls, calendar, invoice, ...) had no cancel button.
  // Default to a self-contained handler; callers that pass their own keep their behavior.
  const runCancelAction = async (
    action: () => Promise<unknown>,
    successMsg: string,
    errorMsg: string,
  ) => {
    try {
      await action();
      addNotification(notificationService.success(successMsg));
      queryClient.invalidateQueries({ queryKey: ['job', resolvedJobId] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      layer.onClose();
    } catch (error: any) {
      addNotification(notificationService.error(error?.response?.data?.details || errorMsg));
      throw error;
    }
  };

  const onCancelJob = props.onCancelJob ?? ((job: JobDTO, reason?: string) =>
    runCancelAction(() => apiService.cancelJob(job.id, reason), 'Job canceled successfully', 'Failed to cancel job'));

  const onUncancelJob = props.onUncancelJob ?? ((job: JobDTO) =>
    runCancelAction(() => apiService.uncancelJob(job.id), 'Job un-canceled successfully', 'Failed to un-cancel job'));

  const { data: fetchedJob, isError, refetch } = useQuery({
    queryKey: ['job', resolvedJobId],
    queryFn: () => apiService.getJobById(resolvedJobId!),
    enabled: !!resolvedJobId,
    // Don't retry client errors (e.g. 404 when the job belongs to a company the user can't access —
    // reachable from the calls page on a shared number). Surface a message instead of spinning.
    retry: (failureCount, err: any) => {
      const status = err?.response?.status;
      if (status && status >= 400 && status < 500) return false;
      return failureCount < 3;
    },
  });

  const isOnline = useOnlineStatus();

  // Always wait for the full JobDTO from getJobById — never pass the lean DTO to detail components
  if (!fetchedJob) {
    if (!isOpen) return null;
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black/20 backdrop-blur-sm" style={layer.style}>
        <div className="bg-white p-4 rounded-lg shadow-xl">
          {!isOnline ? (
            // Offline with no cached copy of this job — the query is paused and would
            // otherwise spin forever. Show an actionable fallback instead.
            <OfflineFallback onRetry={() => refetch()} onBack={layer.onClose} />
          ) : isError ? (
            // Job couldn't be loaded — most often a 404 because it belongs to a company the user
            // can't access (e.g. opened from a shared-number call). Don't spin forever.
            <div className="flex flex-col items-center gap-3 text-center max-w-xs px-4 py-2">
              <SearchX className="h-8 w-8 text-secondary-400" />
              <p className="font-medium text-secondary-800">Job not found</p>
              <p className="text-sm text-secondary-500">
                This job may belong to a different company or no longer exist.
              </p>
              <button
                onClick={layer.onClose}
                className="mt-1 px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700"
              >
                Close
              </button>
            </div>
          ) : (
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          )}
        </div>
      </div>
    );
  }

  const effectiveProps = {
    ...props,
    job: fetchedJob,
    isOpen,
    onCancelJob,
    onClose: layer.onClose,
    /** Close without history.back() — for in-app links that manage history themselves. */
    onDismiss: props.onClose,
    layer,
  };

  if (isMobile) {
    return (
      <LayerDepthContext.Provider value={layer.depth}>
        <JobDetailsMobile {...effectiveProps} onUncancelJob={onUncancelJob} />
      </LayerDepthContext.Provider>
    );
  }

  return (
    <LayerDepthContext.Provider value={layer.depth}>
      <JobDetailsDesktop {...effectiveProps} onUncancelJob={onUncancelJob} />
    </LayerDepthContext.Provider>
  );
}
